import { useForm } from 'react-hook-form'
import { DisruptionFormValues } from '../types'
import { DisruptionStatus } from '@/types/disruptions'
import { useCreateDisruption } from '../api/useCreateDisruption'
import { useEffect, useState } from 'react'
import { useClickOutside } from '@/helpers/useClickOutside'
import { Table } from '@tanstack/react-table'
import { Disruption } from '@/types/disruptions'
import { useDisruptionsTableFormStateStore } from '../store'
import { useUpdateDisruption } from '../api/useUpdateDisruption'
import { ModalTypes, useModal } from '@/components/Modal'
import { useNotification } from '@/ui/Notification'

export const useDisruptionForm = ({ table }: { table: Table<Disruption> }) => {
    const [isCreateFormActive, setIsCreateFormActive] = useState(false)
    const { handleCreateDisruption, isPending: isSubmitting } = useCreateDisruption()
    const { setSelectedRoutes, selectedRoutes, selectedRowId, clearInputValues } = useDisruptionsTableFormStateStore()
    const { handleUpdateDisruption } = useUpdateDisruption()

    const { openModal } = useModal()
    const { showNotification } = useNotification()

    const selectedDisruption = table.getRowModel().rows.find((row) => row.getIsSelected())

    const form = useForm<DisruptionFormValues>({
        defaultValues: {
            status: DisruptionStatus.Live,
            reference: '',
            routes: '',
            location: '',
            details: '',
            disruptionType: '',
            nmccInformed: '',
            delayInMinutes: '',
            diversionRoutes: [{ diversion: '', route: '' }],
            start: '',
            end: '',
        },
    })

    const { isDirty } = form.formState

    useEffect(() => {
        if (selectedDisruption) {
            setSelectedRoutes(selectedDisruption.original.routes.flatMap((route) => route.route))

            form.reset({
                status: selectedDisruption.original.status,
                reference: selectedDisruption.original.reference,
                routes: selectedDisruption.original.routes.flatMap((route) => route.route).join(', '),
                location: selectedDisruption.original.location,
                details: selectedDisruption.original.diversion,
                diversionRoutes: [
                    ...selectedDisruption.original.routes.map((route) => ({
                        diversion: route.diversion ?? '',
                        route: route.route ?? '',
                    })),
                    {
                        diversion: '',
                        route: '',
                    },
                ],
                disruptionType: selectedDisruption.original.disruptionType,
                nmccInformed:
                    selectedDisruption.original.nmccInformed === null
                        ? ''
                        : selectedDisruption.original.nmccInformed === true
                          ? 'Yes'
                          : 'No',
                delayInMinutes:
                    selectedDisruption.original.delay === null ? '' : selectedDisruption.original.delay.toString(),
                start: selectedDisruption.original.start
                    ? new Date(selectedDisruption.original.start).toISOString()
                    : '',
                end: selectedDisruption.original.end ? new Date(selectedDisruption.original.end).toISOString() : '',
            })
        } else {
            form.reset({
                status: DisruptionStatus.Live,
                reference: '',
                routes: '',
                location: '',
                details: '',
                disruptionType: '',
                nmccInformed: '',
                delayInMinutes: '',
                diversionRoutes: [{ diversion: '', route: '' }],
                start: '',
                end: '',
            })
            setSelectedRoutes([])
        }
    }, [selectedDisruption])

    const handleClickAwayFromForm = () => {
        if (isCreateFormActive) {
            // check if route field is empty
            if (selectedRoutes.length === 0) {
                openModal(ModalTypes.addRouteModal, {
                    title: 'Add route',
                    message: <>Routes must be added before the disruption can be saved.</>,
                    onAddRoute: () => {
                        setTimeout(() => {
                            form.setFocus('routes')
                        }, 100)
                    },
                    onCancel: () => {
                        form.reset()
                        clearInputValues()
                        setIsCreateFormActive(false)
                        showNotification({ message: 'Disruption deleted' })
                    },
                })
                return
            }

            form.handleSubmit((data) => handleCreateDisruption(data))()
            form.reset()
        }
        setIsCreateFormActive(false)
    }

    const wrapperRef = useClickOutside<HTMLDivElement>(handleClickAwayFromForm, true)

    const handleClickAwayFromEditForm = () => {
        if (Object.keys(table.getState().rowSelection).length !== 0) {
            // Compare selectedRoutes (Zustand) against the original disruption routes,
            // because pointerdown fires before the routes input blur commits to the form,
            // so isDirty alone won't reflect route changes in this path.
            const originalRoutes = selectedDisruption?.original.routes.flatMap((r) => r.route).join(', ') ?? ''
            const routesChanged = selectedRoutes.join(', ') !== originalRoutes

            if (isDirty || routesChanged) {
                form.handleSubmit((data) => {
                    try {
                        handleUpdateDisruption(data, selectedRowId, selectedRoutes)
                    } catch (error) {
                        showNotification({
                            type: 'error',
                            title: 'Changes not saved',
                            message:
                                "We're sorry, something unexpected has happened. Please check your work, as not all changes may have been saved.",
                            manualDismiss: true,
                        })

                        // Log to application insights too
                        throw error
                    }
                })()
            }
            table.setRowSelection({})
        }
        form.reset()
    }

    const editWrapperRef = useClickOutside<HTMLTableRowElement>(handleClickAwayFromEditForm, true)

    return {
        form,
        wrapperRef,
        isCreateFormActive,
        setIsCreateFormActive,
        editWrapperRef,
        handleClickAwayFromForm,
        isSubmitting,
    }
}
